import { createSelector, createSlice } from "@reduxjs/toolkit";
import { TENSION_PILE } from "../../constants";
import { berechneErmuedung } from "../../services/Inner/berechneErmuedung.service";
import berechneInnereTragfaehigkeit from "../../services/Inner/berechneInnereTragfaehigkeit.service";
import { berechneKnickenDinEN1993 } from "../../services/Inner/berechneKnickenDinEN1993.service";
import { reset } from "../resetParameters";
import { selectBelastungsartUndRichtung } from "./belastungsartUndRichtungSlice";
import {
  BemessungenMikropfaehle,
  selectBemessungenMikropfaehle,
} from "./bemessungenMikropfaehleSlice";
import { selectBemessungslast } from "./bemessungslastSlice";
import { selectBohrkronendurchmesser } from "./bohrkronendurchmesserSlice";
import { selectFrontendZustand } from "./frontendZustandSlice";
import { selectTragglied } from "./traggliedSlice";

export const BemessungssituationState = Object.freeze({
  DAUERHAFT: 0,
  TEMPORÄR: 1,
});

const initialState = {
  bemessungssituation: BemessungssituationState.DAUERHAFT,
  teilsicherheitsbeiwertStahltragglied: 1.15, // Teilsicherheitsbeiwert Stahltragglied
  aufweitungNnachDIN_SPEC_18539: 20, // Aufweitung nach DIN SPEC 18539

  koeffizientNachWenz: 9, // Koeffizient nach Wenz
  koeffizientNachGudehus: 0.1, // Koeffizient nach Gudehus
  staerkeDesZylinders: 0.035, // Stärke des Zylinders
  querschnittsnachweis: 0,

  biegesteifigkeit: 2,
  knicken: {
    ofner: {
      teilsicherheitsbeiwertFuerGeotechnischenKenngroessen: 1, // Teilsicherheitsbeiwert für geotechnischen Kenngrößen
      teilsicherheitsbeiwertZement: 1.5, // Teilsicherheitsbeiwert Zement
      verpresskoerperdurchmesser: Number.NaN,
      undraenierteScherfestigkeit: Number.NaN,
    },
    dinEN: {
      dickeWeicheBodenschicht: Number.NaN,
      knickkoeffizient: 0.7,
    },
  },
  ermuedung: {
    schwingungsObergrenze: Number.NaN,
    schwingungsUntergrenze: Number.NaN,
    anzahlLastwechsel: Number.NaN,
    teilsicherheitsbeiwertFuerErmuedung: 1.15,
    teilsicherheitsbeiwertFuerErmuedungslastenDinEN1991: 1,
  },
  carryingElementErrorInNachweis: false,
};

const innereSlice = createSlice({
  name: "innere",
  initialState,
  reducers: {
    setInnereState(state, action) {
      state.bemessungssituation = action.payload.bemessungssituation;
      state.teilsicherheitsbeiwertStahltragglied =
        action.payload.teilsicherheitsbeiwertStahltragglied;
      state.aufweitungNnachDIN_SPEC_18539 = action.payload.aufweitungNnachDIN_SPEC_18539;
      state.koeffizientNachWenz = action.payload.koeffizientNachWenz;
      state.koeffizientNachGudehus = action.payload.koeffizientNachGudehus;
      state.staerkeDesZylinders = action.payload.staerkeDesZylinders;
      state.querschnittsnachweis = action.payload.querschnittsnachweis;

      state.knicken = action.payload.knicken;
      state.ermuedung = action.payload.ermuedung;
    },
    setCarryingElementErrorInNachweis(state, action) {
      state.carryingElementErrorInNachweis = action.payload;
    },
    setQuerschnittsnachweis(state, action) {
      state.querschnittsnachweis = action.payload;
    },
    setVerpresskoerperdurchmesser(state, action) {
      state.knicken.ofner.verpresskoerperdurchmesser = action.payload;
    },
    setUndraenierteScherfestigkeit(state, action) {
      state.knicken.ofner.undraenierteScherfestigkeit = action.payload;
    },
    setTeilsicherheitsbeiwertStahltragglied(state, action) {
      state.teilsicherheitsbeiwertStahltragglied = action.payload;
    },
    setDickeWeicheBodenschicht(state, action) {
      state.knicken.dinEN.dickeWeicheBodenschicht = action.payload;
    },
    setKnickkoeffizient(state, action) {
      state.knicken.dinEN.knickkoeffizient = action.payload;
    },
    setSchwingungsObergrenze(state, action) {
      state.ermuedung.schwingungsObergrenze = action.payload;
    },
    setSchwingungsUntergrenze(state, action) {
      state.ermuedung.schwingungsUntergrenze = action.payload;
    },
    setAnzahlLastwechsel(state, action) {
      state.ermuedung.anzahlLastwechsel = action.payload;
    },
    setTeilsicherheitsbeiwertFuerErmuedung(state, action) {
      state.ermuedung.teilsicherheitsbeiwertFuerErmuedung = action.payload;
    },
    setBemessungssituation(state, action) {
      state.bemessungssituation = Number(action.payload);
    },
    setTeilsicherheitsbeiwertZement(state, action) {
      state.knicken.ofner.teilsicherheitsbeiwertZement = action.payload;
    },
    setTeilsicherheitsbeiwertFuerErmuedungslastenDinEN1991(state, action) {
      state.ermuedung.teilsicherheitsbeiwertFuerErmuedungslastenDinEN1991 = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(reset, (state, action) => {
      return initialState;
    });
  },
});

export const {
  setInnereState,
  setCarryingElementErrorInNachweis,
  setQuerschnittsnachweis,
  setVerpresskoerperdurchmesser,
  setUndraenierteScherfestigkeit,
  setTeilsicherheitsbeiwertStahltragglied,
  setDickeWeicheBodenschicht,
  setKnickkoeffizient,
  setSchwingungsObergrenze,
  setSchwingungsUntergrenze,
  setAnzahlLastwechsel,
  setTeilsicherheitsbeiwertFuerErmuedung,
  setBemessungssituation,
  setTeilsicherheitsbeiwertZement,
  setTeilsicherheitsbeiwertFuerErmuedungslastenDinEN1991,
} = innereSlice.actions;

export default innereSlice.reducer;

export const selectCarryingElementErrorInNachweis = (state) =>
  state.innere.carryingElementErrorInNachweis;
export const selectQuerschnittsnachweis = (state) => state.innere.querschnittsnachweis;
export const selectBemessungssituation = (state) => state.innere.bemessungssituation;
export const selectAufweitungNnachDIN_SPEC_18539 = (state) =>
  state.innere.aufweitungNnachDIN_SPEC_18539;
export const selectTeilsicherheitsbeiwert = (state) =>
  state.innere.teilsicherheitsbeiwertStahltragglied;

export const selectTeilsicherheitsbeiwertZement = (state) =>
  state.innere.knicken.ofner.teilsicherheitsbeiwertZement;
export const selectKoeffizientNachWenz = (state) => state.innere.koeffizientNachWenz;
export const selectKoeffizientNachGudehus = (state) => state.innere.koeffizientNachGudehus;
export const selectTeilsicherheitsbeiwertFuerGeotechnischenKenngroessen = (state) =>
  state.innere.knicken.ofner.teilsicherheitsbeiwertFuerGeotechnischenKenngroessen;
export const selectStaerkeDesZylinders = (state) => state.innere.staerkeDesZylinders;
export const selectKnickkoeffizient = (state) => state.innere.knicken.dinEN.knickkoeffizient;
export const selectBiegesteifigkeit = (state) => state.innere.biegesteifigkeit;
export const selectVerpresskoerperdurchmesser = (state) =>
  state.innere.knicken.ofner.verpresskoerperdurchmesser;
export const selectUndraenierteScherfestigkeit = (state) =>
  state.innere.knicken.ofner.undraenierteScherfestigkeit;

export const selectDickeWeicheBodenschicht = (state) =>
  state.innere.knicken.dinEN.dickeWeicheBodenschicht;

export const selectSchwingungsObergrenze = (state) => state.innere.ermuedung.schwingungsObergrenze;
export const selectSchwingungsUntergrenze = (state) =>
  state.innere.ermuedung.schwingungsUntergrenze;
export const selectAnzahlLastwechsel = (state) => state.innere.ermuedung.anzahlLastwechsel;
export const selectTeilsicherheitsbeiwertFuerErmuedung = (state) =>
  state.innere.ermuedung.teilsicherheitsbeiwertFuerErmuedung;
export const selectTeilsicherheitsbeiwertFuerErmuedungslastenDinEN1991 = (state) =>
  state.innere.ermuedung.teilsicherheitsbeiwertFuerErmuedungslastenDinEN1991;

export const selectBerechneInnere = (traggliedDaten) => {
  return createSelector(
    [
      selectBemessungssituation,
      selectTragglied,
      selectBemessungslast,
      selectBohrkronendurchmesser,
      selectBelastungsartUndRichtung,
      selectBemessungenMikropfaehle,
      selectTeilsicherheitsbeiwert,
    ],
    (
      bemessungssituation,
      tragglied,
      bemessungslast,
      bohrkronendurchmesser,
      belastungsartUndRichtung,
      bemessungenMikropfaehle,
      teilsicherheitsbeiwertStahltragglied
    ) => {
      let result = berechneInnereTragfaehigkeit({
        bemessungenMikropfaehle: bemessungenMikropfaehle,
        bemessungssituation: bemessungssituation,
        tragglied: tragglied,
        bemessungslast: bemessungslast,
        bohrkronendurchmesser: bohrkronendurchmesser,
        belastungsartUndRichtung: belastungsartUndRichtung,
        traggliedDaten: traggliedDaten,
        teilsicherheitsbeiwert: teilsicherheitsbeiwertStahltragglied,
      });

      return result;
    }
  );
};
// Error for Wahl des Stahltraggliedes
export const selectDisplayUtilizationError = (traggliedDaten) => {
  return createSelector(
    [
      selectIsUtilizationFactorValid_Statik(traggliedDaten),
      selectIsUtilizationFactorValid_Ermuedung(traggliedDaten),
      selectBohrkronendurchmesser,
      selectFrontendZustand,
    ],
    (isValidStatik, isValidErmuedung, bohrkronendurchmesser, { ermuedungCheckbox }) => {
      let isErrorInStatik = !isValidStatik && bohrkronendurchmesser != 0 ? true : false;
      let isErrorInErmuedung = !isValidErmuedung;

      return isErrorInStatik || (ermuedungCheckbox && isErrorInErmuedung);
    }
  );
};

// Statik
export const selectIsUtilizationFactorValid_Statik = (traggliedDaten) => {
  return createSelector([selectBerechneInnere(traggliedDaten)], (innereData) => {
    if (innereData.fixedN === "") return false;
    if (Number(innereData.fixedN) === Number.NaN) return false;

    return innereData.fixedN >= 0.0 && innereData.fixedN <= 1.0 ? true : false;
  });
};

// Knicken DinEN
function isValidNumber(inputVal) {
  if (String(inputVal).trim() == "") return false;
  // TODO: @Alex warum ist das nötig ?
  return typeof Number(inputVal) == "number" && !isNaN(inputVal) ? true : false;
}

export const selectBerechneKnickenDinEN1993 = (traggliedDaten) => {
  return createSelector(
    [
      selectKnickkoeffizient,
      selectDickeWeicheBodenschicht,
      selectBemessungslast,
      selectTeilsicherheitsbeiwert,
    ],
    (
      knickkoeffizient,
      dickeWeicheBodenschicht,
      bemessungslast,
      teilsicherheitsbeiwertStahltragglied
    ) => {
      // no userinput or invalid
      if (dickeWeicheBodenschicht == 0 || !isValidNumber(dickeWeicheBodenschicht)) {
        // reset
        // TODO: shouldnt this be undefined ?
        return {
          berechneteKnickenWerte: null,
          delta: null,
          abminderungsfaktor: null,
          biegeknickbeanspruchbarkeit: null,
          ausnutzung: null,
        };
      }

      let KnickenDinEN1993Result = berechneKnickenDinEN1993(
        Number(knickkoeffizient),
        traggliedDaten,
        dickeWeicheBodenschicht,
        Number(bemessungslast),
        teilsicherheitsbeiwertStahltragglied
      );

      return KnickenDinEN1993Result;
    }
  );
};

export const selectBerechneErmuedung = (traggliedDaten) => {
  return createSelector(
    [
      selectSchwingungsObergrenze,
      selectSchwingungsUntergrenze,
      selectTeilsicherheitsbeiwertFuerErmuedung,
      selectTeilsicherheitsbeiwertFuerErmuedungslastenDinEN1991,
      selectAnzahlLastwechsel,
    ],
    (
      schwingungsObergrenze,
      schwingungsUntergrenze,
      teilsicherheitsbeiwertFuerErmuedung,
      teilsicherheitsbeiwertFuerErmuedungslastenDinEN1991,
      anzahlLastwechsel
    ) => {
      let berechneteErmuedungsWerte = berechneErmuedung(
        traggliedDaten?.querschnitt,
        Number(anzahlLastwechsel),
        Number(teilsicherheitsbeiwertFuerErmuedung),
        Number(schwingungsObergrenze),
        Number(schwingungsUntergrenze),
        Number(teilsicherheitsbeiwertFuerErmuedungslastenDinEN1991)
      );
      return berechneteErmuedungsWerte;
    }
  );
};

export const selectIsUtilizationFactorValid_Ermuedung = (traggliedDaten) => {
  return createSelector([selectBerechneErmuedung(traggliedDaten)], (ermuedungsDaten) => {
    // If we can't calculate -> is valid
    if (
      ermuedungsDaten?.ausnutzungCalc === null ||
      ermuedungsDaten?.ausnutzungCalc === undefined ||
      isNaN(ermuedungsDaten?.ausnutzungCalc)
    ) {
      return true;
    }
    return (
      Number(Number(ermuedungsDaten?.ausnutzungCalc).toFixed(2)) <= 1.0 &&
      Number(Number(ermuedungsDaten?.ausnutzungCalc).toFixed(2)) >= 0.0
    );
  });
};

/* TODO: for later extraction of calculation
// nested, as example
// https://github.com/reduxjs/reselect/issues/100#issuecomment-201752563
export const selectorAAA = (traggliedDaten) => {
  return createSelector([selectBerechneInnere(traggliedDaten)], (entity) => {
    console.log("test", entity);
  });
};
*/
